/**
 * Service for audio recording, WebSocket communication, and transcription processing.
 *
 * @module utils/TranscriptService
 * @author Snowfly
 * @version 1.0.0
 */
import {
    convertAudioToBinaryMessage,
    handleEventStreamMessage,
    resetEventStreamMessage,
    getAudioEventMessage,
} from "./convertIntoBuffer";
import {EventStreamCodec} from "@aws-sdk/eventstream-codec";
import {isUrlExpired} from "./url";
import {getAwsPresignedUrl} from "../services/result.service";
import {logError} from "../redux/sims/actions";
import MicrophoneStream from "microphone-stream";

const util_utf8_node = require("@aws-sdk/util-utf8-node"); // utilities for encoding and decoding UTF8

const eventStreamMarshaller = new EventStreamCodec(
    util_utf8_node.toUtf8,
    util_utf8_node.fromUtf8
);

/**
 * TranscriptService handles audio recording, WebSocket communication,
 * and transcription processing.
 *
 * This service captures audio input from the microphone, sends audio data
 * to a server via WebSocket, and processes transcription messages received
 * from the server.
 */
export class TranscriptService {
    blobs = [];
    micStream = null;
    stream = null;
    audioStream = null;
    webSocket = null;
    transcription = '';
    transcriptReadyCallback = null;
    errorCallback = null;
    setPresignedUrl = null;

    /**
     * Initializes an instance of the class.
     *
     * @param {string} presignedUrl The presigned URL used for accessing the resource.
     * @param {function} transcriptReadyCallback The callback function to execute when the
     *     transcript is ready.
     * @param {function} errorCallback The callback function to execute in case of an error.
     * @param {function} setPresignedUrl A function to update or set the presigned URL.
     * @return {void}
     */
    constructor(
        presignedUrl,
        transcriptReadyCallback,
        errorCallback,
        setPresignedUrl
    ) {
        this.presignedUrl = presignedUrl;
        this.transcriptReadyCallback = transcriptReadyCallback;
        this.errorCallback = errorCallback;
        this.setPresignedUrl = setPresignedUrl;
    }

    /**
     * Creates and initializes a new instance of the TranscriptService with the provided
     * parameters.
     * Sets up audio streaming, WebSocket connectivity, and event handlers for handling
     * transcripts.
     *
     * @param {string} presignedUrl - The presigned URL for WebSocket connection initialization.
     * @param {function} transcriptReadyCallback - A callback function triggered when the
     *     transcript is ready.
     * @param {function} errorCallback - A callback function triggered in case of an error during
     *     transcription.
     * @param {function} setPresignedUrl - A function to update the presigned URL for the WebSocket
     *     connection.
     * @return {Promise<TranscriptService>} A promise that resolves to a fully-initialized instance
     *     of TranscriptService.
     */
    static async createTranscriptService(
        presignedUrl,
        transcriptReadyCallback,
        errorCallback,
        setPresignedUrl
    ) {
        let service = new TranscriptService(
            presignedUrl,
            transcriptReadyCallback,
            errorCallback,
            setPresignedUrl
        );
        let voiceStream = await navigator.mediaDevices.getUserMedia(
            {
                video: false,
                audio: true,
            }
        );
        service.micStream = new MicrophoneStream(); // ws
        service.stream = new MediaStream(voiceStream);
        let ctx = new AudioContext();
        const src = ctx.createMediaStreamSource(service.stream);
        const dst = ctx.createMediaStreamDestination();
        const gain = ctx.createGain();

        src.connect(gain);
        gain.connect(dst);

        gain.gain.value = 1;

        service.micStream.setStream(dst.stream);//voiceStream); // ws

        service.audioStream = new MediaRecorder(voiceStream, { mimeType: "audio/webm" });
        service.transcriptReadyCallback = transcriptReadyCallback;
        service.errorCallback = errorCallback;
        service.setPresignedUrl = setPresignedUrl;

        service._initWebSocketUrl(presignedUrl);
        service._initEventHandlers();

        return service;
    }

    /**
     * Starts the audio stream by invoking the start method on the audioStream object.
     * @return {void} Does not return a value.
     */
    start() {
        if (this.audioStream.state !== 'recording') {
            this.audioStream.start();
        }
    }

    /**
     * Stops the microphone and audio streams by unregistering event listeners
     * and halting the audio stream processing.
     *
     * @return {void} This method does not return a value.
     */
    stop() {
        if (this.audioStream.state === 'recording') {
            this.micStream.off('data', this.onAudioStreamDataAvailable2.bind(this));
            this.micStream.stop();
            this.audioStream.stop();
        }
    }

    /**
     * Initializes the WebSocket connection URL. Determines whether the provided presigned URL
     * is expired and fetches a new one if necessary. The validated or fetched URL is then
     * set and used to establish the WebSocket connection.
     *
     * @param {string} presignedUrl - The presigned URL to check for expiration and use
     *                                for the WebSocket connection.
     * @return {void} This method does not return a value.
     */
    async _initWebSocketUrl(presignedUrl) {
        if (!this.setPresignedUrl) {
            console.error('setPresignedUrl is not defined');
            return;
        }

        let preUrl;

        if (isUrlExpired(presignedUrl)) {
            let res = await getAwsPresignedUrl();
            preUrl = res.data.url;
        } else {
            preUrl = presignedUrl;
        }

        this.setPresignedUrl(preUrl);
        this.webSocket = new WebSocket(preUrl);
        this.webSocket.binaryType = "arraybuffer";
    }

    /**
     * Initializes the event handlers for the WebSocket and audio stream.
     *
     * This method binds the respective event listener callback functions to each
     * of the required events for the WebSocket and audio stream objects. It ensures
     * proper event handling for the connection lifecycle, message processing, and
     * audio stream events.
     *
     * @return {void} No value is returned by this method.
     */
    _initEventHandlers() {
        this.webSocket.onopen = this.onWebSocketOpen.bind(this);
        this.webSocket.onerror = this.onWebSocketError.bind(this);
        this.webSocket.onclose = this.onWebSocketClose.bind(this);
        this.webSocket.onmessage = this.onWebSocketMessage.bind(this);
        this.audioStream.onstop = this.onAudioStreamStop.bind(this);
        this.audioStream.ondataavailable = this.onAudioStreamDataAvailable.bind(this);
    }

    /**
     * Closes the WebSocket connection if it is open, or cleans up if the connection is already
     * closed. Ensures that the WebSocket object is set to null and stops any related processes.
     *
     * @return {Promise<void>} Resolves when the WebSocket is closed and cleanup is complete.
     */
    async _closeSocket() {
        if (this.webSocket.readyState === WebSocket.OPEN) {
            this.webSocket.close();
            this.webSocket = null;
        } else if (this.webSocket.readyState === WebSocket.CLOSED) {
            this.webSocket = null;
        }
        this.stop();
    }

    /**
     * Event listener for the WebSocket 'open' event.
     *
     * This event is triggered when a WebSocket connection is successfully established.
     * You can use this event to perform actions that should occur when the connection becomes
     * open, such as sending an initial message or logging the successful connection.
     *
     * @event ws.onopen
     * @param {Event} evt - The event object associated with the WebSocket 'open' event.
     */
    onWebSocketOpen (/* evt */) {
        resetEventStreamMessage();

        this.micStream.on('data', this.onAudioStreamDataAvailable2.bind(this));
    }

    /**
     * Event handler for the 'error' event on a WebSocket instance.
     *
     * Triggered when an error occurs during the WebSocket connection
     * or communication, such as an issue with the connection handshake
     * or a data transfer problem.
     *
     * @callback ws.onerror
     * @param {Event} evt - The event object containing details about the error.
     */
    onWebSocketError (evt) {
        if (this.errorCallback) {
            this.errorCallback(evt);
        } else {
            console.error('WebSocket error occurred', evt);
        }
    }

    /**
     * Event handler for the 'onclose' event of a WebSocket connection.
     *
     * This event is triggered when the WebSocket connection is closed, either
     * by the client or the server, or due to an error or network issue.
     *
     * The `onclose` event provides access to the CloseEvent object, which
     * contains details about the closure, such as the status code and reason.
     *
     * @callback ws.onclose
     * @param {CloseEvent} evt - An object containing information about the reason
     *                             for the WebSocket closure, including the status
     *                             code and optional reason.
     */
    async onWebSocketClose (evt) {
        if (evt.code !== WebSocket.OPEN) {
            console.log('WebSocket Closed abnormally');
        }

        this.stop();

        if (this.transcriptReadyCallback) {
            /* console.log('transcription = %s', this.transcription);
            console.log('blobs = %o', this.blobs); */

            await this.transcriptReadyCallback(this.transcription, this.blobs);
        } else {
            console.error('Transcript ready callback is not set, cannot provide transcription results: %s', this.transcription);
        }
        await this._closeSocket();

        this.blobs = [];
        resetEventStreamMessage();
    }

    /**
     * Event handler for the 'message' event in a WebSocket connection.
     * Triggered when a message is received from the server through the WebSocket.
     *
     * @callback ws.onmessage
     * @param {MessageEvent} evt - The message event object containing the received data.
     * @property {string} evt.data - The data sent by the server. Could be a string or binary
     *     data.
     */
    onWebSocketMessage (evt) {
        let messageWrapper = eventStreamMarshaller.decode(Buffer.from(evt.data));
        let messageBody = JSON.parse(
            String.fromCharCode.apply(String, messageWrapper.body)
        );
        let transcription/* , partial, isFinal */;

        switch (messageWrapper.headers[":message-type"].value) {
            case 'event':
                [transcription/* , partial, isFinal */] = handleEventStreamMessage(
                    messageBody,
                    this.transcription
                );
                this.transcription = transcription;
                /* console.log('messageBody = %o', messageBody);
                console.log('Partial = %s', partial);
                console.log('transcription = %s', transcription);
                console.log('isFinal = %s', isFinal); */
            break;

            case 'exception':
                console.error('WS Error = %o', messageBody);
                console.error('error time' + new Date());
                logError({'logError': 'Error on the frontend cause by web socket: ' + messageBody.Message});
                if (this.errorCallback) {
                    this.errorCallback(messageBody);
                } else {
                    this.onWebSocketError(messageBody);
                }
            break;

            default:
                //console.debug('WebSocket message: %o', messageBody);
        }
    }

    /**
     * Handles the availability of audio stream data and sends it to an open WebSocket connection.
     *
     * @param {Object} evt - The event containing audio stream data that needs to be processed and
     *     sent.
     * @return {void} This method does not return a value.
     */
    onAudioStreamDataAvailable2 (evt) {
        if (!this.webSocket) {
            //console.error('WebSocket is NULL!!!!');
            return;
        }

        const buffer = convertAudioToBinaryMessage(evt);
        if ((this.webSocket?.readyState === WebSocket.OPEN)) {
            //console.log('Sent buffer to transcription service');
            this.webSocket.send(buffer);
        }
    }

    /**
     * Handles the event triggered when audio stream data becomes available.
     *
     * @param {Event} evt - The event object containing audio stream data.
     * @return {void} This method does not return a value.
     */
    onAudioStreamDataAvailable(evt) {
        if (evt.data) {
            //console.log('onAudioStreamDataAvailable evt = %o', evt);
            this.blobs.push(evt.data);
        }
    }

    /**
     * Handles the event triggered when an audio stream stops.
     *
     * @param {Object} evt - The event object containing details about the stopped audio stream.
     * @return {void} This method does not return a value.
     */
    onAudioStreamStop(evt) {
        //console.log('onAudioStreamStop evt = %o', evt);
        this.onSocketClosed(evt);
    }

    /**
     * Handles the event when the WebSocket connection is closed.
     *
     * @param {CloseEvent} evt - The event object associated with the WebSocket close event.
     * @return {void} This method does not return a value.
     */
    onSocketClosed(/* evt */) {
        //console.log('onSocketClosed evt = %o', evt);
        let emptyMessage = getAudioEventMessage(Buffer.alloc(0));
        let emptyBuffer = eventStreamMarshaller.encode(emptyMessage);//marshall(emptyMessage);
        if (this.webSocket.readyState === WebSocket.OPEN) {
            this.webSocket.send(emptyBuffer);
        }
    }

}

export default TranscriptService;
